import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Tooltip, Modal, Typography } from 'antd';
import { HistoryOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import mapApi from 'api/map';
import moment from 'moment';
import { JOB_COMPLETED, JOB_FAILED, JOB_PENDING, JOB_PROCESSING } from 'constants/index';

const { Text } = Typography;

const ImportHistory = ({ visible, onClose }) => {
    const { t } = useTranslation();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchImportJobs = async () => {
        try {
            setLoading(true);
            const response = await mapApi.getImportJobs({ limit: 50 });
            setJobs(response.data || []);
        } catch (error) {
            console.error('Error fetching import jobs:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible) {
            fetchImportJobs();
        }
    }, [visible]);

    const getStatusColor = (status) => {
        switch (status) {
            case JOB_PENDING:
                return 'orange';
            case JOB_PROCESSING:
                return 'blue';
            case JOB_COMPLETED:
                return 'green';
            case JOB_FAILED:
                return 'red';
            default:
                return 'default';
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        return moment(dateString).format('YYYY-MM-DD HH:mm:ss');
    };

    const columns = [
        {
            title: t('type'),
            dataIndex: 'type',
            key: 'type',
            render: (type) => (
                <Tag color={type === 'shop' ? 'blue' : 'green'}>
                    {type === 'shop' ? t('shop') : t('cabinetSingle')}
                </Tag>
            ),
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {t(status)}
                </Tag>
            ),
        },
        {
            title: t('progress'),
            dataIndex: 'progress',
            key: 'progress',
            render: (progress) => {
                if (!progress) return '-';
                const { total, processed, success, failed } = progress;
                return (
                    <div>
                        <Text>{processed || 0}/{total || 0}</Text>
                        {success > 0 && (
                            <Text type="success" style={{ marginLeft: 8 }}>
                                ✓{success}
                            </Text>
                        )}
                        {failed > 0 && (
                            <Text type="danger" style={{ marginLeft: 8 }}>
                                ✗{failed}
                            </Text>
                        )}
                    </div>
                );
            },
        },
        {
            title: t('startedAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: formatDateTime,
        },
        {
            title: t('completedAt'),
            dataIndex: 'completedAt',
            key: 'completedAt',
            render: formatDateTime,
        },
        {
            title: t('error'),
            dataIndex: 'error',
            key: 'error',
            render: (error) => {
                if (!error) return '-';
                return (
                    <Tooltip title={error}>
                        <Text type="danger" ellipsis style={{ maxWidth: 200 }}>
                            {error}
                        </Text>
                    </Tooltip>
                );
            },
        },
    ];

    return (
        <Modal
            title={
                <Space>
                    <HistoryOutlined />
                    {t('importHistory')}
                </Space>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1200}
        >
            <Card
                size="small"
                extra={
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchImportJobs}
                        loading={loading}
                        size="small"
                    >
                        {t('refresh')}
                    </Button>
                }
            >
                <Table
                    columns={columns}
                    dataSource={jobs}
                    rowKey="jobId"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                    }}
                    scroll={{ x: 800 }}
                />
            </Card>
        </Modal>
    );
};

export default ImportHistory;
